/**
 * Toolbar for the UniversalFileViewer with zoom, rotate, download, print, and close controls
 */

import { ActionIcon, Tooltip, Divider, Box, Group, Text, Badge } from '@mantine/core';
import {
  IconZoomIn,
  IconZoomOut,
  IconRotateClockwise,
  IconMaximize,
  IconDownload,
  IconPrinter,
  IconX,
} from '@tabler/icons-react';

interface ViewerToolbarProps {
  fileName: string;
  fileIndex: number;
  totalFiles: number;
  zoom: number;
  canZoom: boolean;
  canRotate: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
  onRotate: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
  onClose: () => void;
}

const iconProps = { size: 18 };
const actionStyle = { color: 'white' } as const;

export function ViewerToolbar({
  fileName,
  fileIndex,
  totalFiles,
  zoom,
  canZoom,
  canRotate,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onRotate,
  onDownload,
  onPrint,
  onClose,
}: ViewerToolbarProps) {
  return (
    <Box
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(8px)',
        padding: '8px 16px',
      }}
    >
      <Group justify="space-between" wrap="nowrap">
        {/* Left: Controls */}
        <Group gap="xs" wrap="nowrap">
          {canZoom && (
            <>
              <Tooltip label="Zoom out (-)">
                <ActionIcon variant="subtle" style={actionStyle} onClick={onZoomOut} disabled={zoom <= 25}>
                  <IconZoomOut {...iconProps} />
                </ActionIcon>
              </Tooltip>
              <Text size="xs" c="white" w={42} ta="center" style={{ userSelect: 'none' }}>
                {zoom}%
              </Text>
              <Tooltip label="Zoom in (+)">
                <ActionIcon variant="subtle" style={actionStyle} onClick={onZoomIn} disabled={zoom >= 300}>
                  <IconZoomIn {...iconProps} />
                </ActionIcon>
              </Tooltip>
              <Divider orientation="vertical" color="rgba(255,255,255,0.2)" />
            </>
          )}

          {canRotate && (
            <>
              <Tooltip label="Rotate (R)">
                <ActionIcon variant="subtle" style={actionStyle} onClick={onRotate}>
                  <IconRotateClockwise {...iconProps} />
                </ActionIcon>
              </Tooltip>
              <Divider orientation="vertical" color="rgba(255,255,255,0.2)" />
            </>
          )}

          {(canZoom || canRotate) && (
            <>
              <Tooltip label="Reset view (0)">
                <ActionIcon variant="subtle" style={actionStyle} onClick={onResetZoom}>
                  <IconMaximize {...iconProps} />
                </ActionIcon>
              </Tooltip>
              <Divider orientation="vertical" color="rgba(255,255,255,0.2)" />
            </>
          )}

          {onDownload && (
            <Tooltip label="Download">
              <ActionIcon variant="subtle" style={actionStyle} onClick={onDownload}>
                <IconDownload {...iconProps} />
              </ActionIcon>
            </Tooltip>
          )}

          {onPrint && (
            <Tooltip label="Print">
              <ActionIcon variant="subtle" style={actionStyle} onClick={onPrint}>
                <IconPrinter {...iconProps} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        {/* Right: File info + Close */}
        <Group gap="sm" wrap="nowrap">
          <Text
            size="sm"
            c="white"
            fw={500}
            lineClamp={1}
            style={{ maxWidth: 300, userSelect: 'none' }}
          >
            {fileName}
          </Text>
          {totalFiles > 1 && (
            <Badge variant="filled" color="dark" size="sm">
              {fileIndex + 1} / {totalFiles}
            </Badge>
          )}
          <Tooltip label="Close (Esc)">
            <ActionIcon variant="subtle" style={actionStyle} size="lg" onClick={onClose}>
              <IconX size={22} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Box>
  );
}
